/**
 * Pipeline set for one (MSAA, scene colour format) pair: figure / prop / hull / keyline (ccw + cw variants), floor,
 * sky, particles and the composite — 13 pipelines, created and `initAsync()`-ed once per tier change (cheatsheet
 * performance rule 3). Draw order is art §3.1's.
 */
import type { TgpuRoot } from 'typegpu';
import { creatureVertexLayout, hullVertexLayout, particleLayout, trailLayout } from './layouts';
import {trailVertex,trailFragment} from '../shaders/trails';
import { creatureFragment, creatureVertex, hullVertex, inkFragment, keylineFragment, keylineVertex, propHullVertex, propVertex } from '../shaders/creature';
import { floorFragment, floorVertex, skyFragment, skyVertex } from '../shaders/scene';
import { compositeFragment, fullscreenVertex } from '../shaders/composite';
import { particleFragment, particleVertex } from '../shaders/particles';
import {illustrationFragment} from '../shaders/illustration';

export const DEPTH_FORMAT = 'depth24plus' as const;

export function buildPipelines(root: TgpuRoot, msaa: 1 | 4, sceneFormat: GPUTextureFormat, canvasFormat: GPUTextureFormat) {
  const multisample = { count: msaa };
  const depthWrite = { format: DEPTH_FORMAT, depthWriteEnabled: true, depthCompare: 'less' } as const;
  const figure = (frontFace: 'ccw' | 'cw') => root.createRenderPipeline({
    attribs: creatureVertexLayout.attrib, vertex: creatureVertex, fragment: creatureFragment,
    targets: { format: sceneFormat }, depthStencil: depthWrite,
    primitive: { topology: 'triangle-list', cullMode: 'back', frontFace }, multisample,
  });
  const prop = (frontFace: 'ccw' | 'cw') => root.createRenderPipeline({
    attribs: creatureVertexLayout.attrib, vertex: propVertex, fragment: creatureFragment,
    targets: { format: sceneFormat }, depthStencil: depthWrite,
    primitive: { topology: 'triangle-list', cullMode: 'back', frontFace }, multisample,
  });
  const hull = (frontFace: 'ccw' | 'cw') => root.createRenderPipeline({
    attribs: hullVertexLayout.attrib, vertex: hullVertex, fragment: inkFragment,
    targets: { format: sceneFormat }, depthStencil: depthWrite,
    primitive: { topology: 'triangle-list', cullMode: 'front', frontFace }, multisample,
  });
  const keyline = (frontFace: 'ccw' | 'cw') => root.createRenderPipeline({
    attribs: hullVertexLayout.attrib, vertex: keylineVertex, fragment: keylineFragment,
    targets: { format: sceneFormat }, depthStencil: depthWrite,
    primitive: { topology: 'triangle-list', cullMode: 'front', frontFace }, multisample,
  });
  const propHull = (frontFace: 'ccw' | 'cw') => root.createRenderPipeline({
    attribs: hullVertexLayout.attrib, vertex: propHullVertex, fragment: inkFragment,
    targets: { format: sceneFormat }, depthStencil: depthWrite,
    primitive: { topology: 'triangle-list', cullMode: 'front', frontFace }, multisample,
  });
  const set = {
    figure: { ccw: figure('ccw'), cw: figure('cw') },
    prop: { ccw: prop('ccw'), cw: prop('cw') },
    hull: { ccw: hull('ccw'), cw: hull('cw') },
    keyline: { ccw: keyline('ccw'), cw: keyline('cw') },
    propHull: { ccw: propHull('ccw'), cw: propHull('cw') },
    floor: root.createRenderPipeline({
      vertex: floorVertex, fragment: floorFragment, targets: { format: sceneFormat }, depthStencil: depthWrite,
      primitive: { topology: 'triangle-list', cullMode: 'none' }, multisample,
    }),
    sky: root.createRenderPipeline({
      vertex: skyVertex, fragment: skyFragment, targets: { format: sceneFormat },
      depthStencil: { format: DEPTH_FORMAT, depthWriteEnabled: false, depthCompare: 'less-equal' }, multisample,
    }),
    illustration: root.createRenderPipeline({
      vertex: skyVertex, fragment: illustrationFragment, targets: {format:sceneFormat},
      depthStencil:{format:DEPTH_FORMAT,depthWriteEnabled:false,depthCompare:'less-equal'},multisample,
    }),
    particles: root.createRenderPipeline({
      attribs: particleLayout.attrib, vertex: particleVertex, fragment: particleFragment,
      targets: {
        format: sceneFormat,
        blend: {
          color: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' },
          alpha: { srcFactor: 'zero', dstFactor: 'one', operation: 'add' }, // keep the figure mask
        },
      },
      depthStencil: { format: DEPTH_FORMAT, depthWriteEnabled: false, depthCompare: 'less' },
      primitive: { topology: 'triangle-list', cullMode: 'none' }, multisample,
    }),
    trails:root.createRenderPipeline({attribs:trailLayout.attrib,vertex:trailVertex,fragment:trailFragment,
      targets:{format:sceneFormat,blend:{color:{srcFactor:'one',dstFactor:'one-minus-src-alpha',operation:'add'},alpha:{srcFactor:'zero',dstFactor:'one',operation:'add'}}},
      depthStencil:{format:DEPTH_FORMAT,depthWriteEnabled:false,depthCompare:'less-equal'},primitive:{topology:'triangle-list',cullMode:'none'},multisample}),
    composite: root.createRenderPipeline({ vertex: fullscreenVertex, fragment: compositeFragment, targets: { format: canvasFormat } }),
  };
  return set;
}
export type PipelineSet = ReturnType<typeof buildPipelines>;

/** Every pipeline of a set, for `initAsync()` at boot. */
export function allPipelines(p: PipelineSet): Array<{ initAsync(): Promise<void> }> {
  return [p.figure.ccw, p.figure.cw, p.prop.ccw, p.prop.cw, p.hull.ccw, p.hull.cw, p.keyline.ccw, p.keyline.cw,
    p.propHull.ccw, p.propHull.cw, p.floor, p.sky, p.illustration, p.particles, p.trails, p.composite];
}
